import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lightbulb, RotateCcw, CheckCircle, ArrowLeft, Menu } from 'lucide-react';
import { auth, db } from '../lib/supabase';
import { LEVELS, NODE_TYPES, ACHIEVEMENTS } from '../data/gameData';
import { ACHIEVEMENT_DEFINITIONS } from '../utils/achievements';
import NodeEditor from '../components/NodeEditor';
import NodeLibrary from '../components/NodeLibrary';
import LevelQuiz from '../components/LevelQuiz';
import NodeConfigSidebar from '../components/NodeConfigSidebar';
import GameCompletionModal from '../components/GameCompletionModal';
import { toast } from 'sonner';
import { useCoursePromo } from '../contexts/CoursePromoContext';
import confetti from 'canvas-confetti';
import useGameStore from '../lib/gameStore';

const LevelPlay = () => {
  const { levelId } = useParams();
  const navigate = useNavigate();
  const { checkTriggers } = useCoursePromo();
  const { profile: userProfile, userAchievements, concepts: learnedConcepts } = useGameStore();
  const [level, setLevel] = useState(null);
  const [nodeTypes, setNodeTypes] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(true);
  const [hintLoading, setHintLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  const [selectedNodeType, setSelectedNodeType] = useState(null);
  const [showNodeConfig, setShowNodeConfig] = useState(false);
  const [showNodeLibrary, setShowNodeLibrary] = useState(false);
  const [showGameCompletionModal, setShowGameCompletionModal] = useState(false);
  const [xpEarned, setXpEarned] = useState(0);

  // Listen for node click events to open sidebar
  useEffect(() => {
    const handleNodeConfig = (e) => {
      setSelectedNodeType(e.detail.nodeType);
      setShowNodeConfig(true);
    };

    window.addEventListener('openNodeConfig', handleNodeConfig);
    return () => window.removeEventListener('openNodeConfig', handleNodeConfig);
  }, []);

  useEffect(() => {
    loadLevel();
    loadNodeTypes();
  }, [levelId]);

  const loadLevel = async () => {
    try {
      // Load level from local game data
      const levelData = LEVELS.find(l => l.id === parseInt(levelId));

      if (!levelData) {
        toast.error('Nivel no encontrado');
        navigate('/dashboard');
        return;
      }

      // Get user to check if level is unlocked
      const { user, error: userError } = await auth.getUser();
      if (userError || !user) {
        navigate('/');
        return;
      }

      // Get user profile to check XP
      const { data: profileData } = await db.profile.get(user.id);

      // Check if level is locked
      if (levelData.required_xp > (profileData?.total_xp || 0)) {
        toast.error('Este nivel aún está bloqueado');
        navigate('/dashboard');
        return;
      }

      setLevel(levelData);
    } catch (error) {
      console.error('Error loading level:', error);
      toast.error('Error al cargar el nivel');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadNodeTypes = async () => {
    try {
      // Load node types from local game data
      setNodeTypes(NODE_TYPES);
    } catch (error) {
      console.error('Error loading node types:', error);
    }
  };

  const handleNodesChange = useCallback((newNodes) => {
    setNodes(newNodes);
  }, []);

  const handleEdgesChange = useCallback((newEdges) => {
    setEdges(newEdges);
  }, []);

  // Client-side validation function
  const validateLevel = (levelData, userNodes, userEdges) => {
    const criteria = levelData.success_criteria;
    const errors = [];

    // Check minimum nodes
    if (userNodes.length < criteria.min_nodes) {
      errors.push(`Se requieren al menos ${criteria.min_nodes} nodos`);
    }

    // Check required nodes
    const nodeTypes = userNodes.map(n => n.data.nodeType);
    for (const requiredType of criteria.required_nodes) {
      if (!nodeTypes.includes(requiredType)) {
        errors.push(`Falta el nodo: ${requiredType}`);
      }
    }

    // Check required connections
    if (userEdges.length < criteria.required_connections) {
      errors.push(`Se requieren al menos ${criteria.required_connections} conexiones`);
    }

    // Check branching if required
    if (criteria.has_branching) {
      // Check if there's at least one node with multiple outgoing connections
      const outgoingCounts = {};
      userEdges.forEach(edge => {
        outgoingCounts[edge.source] = (outgoingCounts[edge.source] || 0) + 1;
      });
      const hasBranch = Object.values(outgoingCounts).some(count => count > 1);
      if (!hasBranch) {
        errors.push('El flujo requiere ramificación (branching)');
      }
    }

    return {
      is_valid: errors.length === 0,
      errors,
      xp_earned: errors.length === 0 ? levelData.reward_xp : 0
    };
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      // Validate using client-side logic
      const result = validateLevel(level, nodes, edges);

      setValidationResult(result);

      if (result.is_valid) {
        // Show quiz before completing
        setShowQuiz(true);
        toast.success('¡Flujo validado! Ahora completa el quiz');
      } else {
        toast.error(result.errors[0] || 'Hay algunos errores en tu flujo');
      }
    } catch (error) {
      console.error('Error validating level:', error);
      toast.error('Error al validar el nivel');
    } finally {
      setValidating(false);
    }
  };

  const handleGetHint = async () => {
    setHintLoading(true);
    try {
      // Generic hints based on level criteria
      const criteria = level.success_criteria;
      const hints = [];

      if (nodes.length < criteria.min_nodes) {
        hints.push(`Necesitas agregar al menos ${criteria.min_nodes} nodos`);
      }

      const nodeTypes = nodes.map(n => n.data.nodeType);
      const missingNodes = criteria.required_nodes.filter(type => !nodeTypes.includes(type));
      if (missingNodes.length > 0) {
        hints.push(`Te faltan estos nodos: ${missingNodes.join(', ')}`);
      }

      if (edges.length < criteria.required_connections) {
        hints.push(`Necesitas conectar los nodos. Se requieren ${criteria.required_connections} conexiones`);
      }

      const hint = hints.length > 0 ? hints[0] : 'Revisa los requisitos del nivel en el tutorial';
      toast.info(hint, { duration: 5000 });
    } catch (error) {
      toast.error('Error al obtener pista');
    } finally {
      setHintLoading(false);
    }
  };

  const handleReset = () => {
    // Clear all nodes and edges
    setNodes([]);
    setEdges([]);
    setValidationResult(null);
    setShowQuiz(false);
    setQuizPassed(false);
    toast.info('Canvas reiniciado');
  };

  const handleQuizComplete = async (passed) => {
    if (!passed) {
      setShowQuiz(false);
      return;
    }

    setQuizPassed(true);

    // Celebration effect
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });

    const xpGained = validationResult?.xp_earned || 0;
    setXpEarned(xpGained);

    try {
      // Get current user
      const { user, error: userError } = await auth.getUser();
      if (userError || !user) {
        toast.error('Error al guardar progreso');
        return;
      }

      // Get current profile
      const { data: currentProfile } = await db.profile.get(user.id);
      if (!currentProfile) {
        toast.error('Error al obtener perfil');
        return;
      }

      // Calculate coins earned (level_id × 10)
      const coinsEarned = parseInt(levelId) * 10;

      // Update or create progress for this level
      await db.progress.upsert(user.id, parseInt(levelId), {
        is_completed: true,
        attempt_count: 1,
        best_score: 100
      });

      // Calculate new totals
      const newTotalXp = (currentProfile.total_xp || 0) + xpGained;
      const newCoins = (currentProfile.coins || 0) + coinsEarned;

      // Update profile with new XP and coins
      await db.profile.update(user.id, {
        total_xp: newTotalXp,
        coins: newCoins,
        current_level: Math.max(currentProfile.current_level || 1, parseInt(levelId))
      });

      // Check and unlock achievements
      const { data: existingAchievements } = await db.achievements.getAll(user.id);
      const unlockedIds = new Set(existingAchievements?.map(a => a.achievement_id) || []);

      // Get all completed levels count
      const { data: allProgress } = await db.progress.getAll(user.id);
      const completedCount = (allProgress?.filter(p => p.is_completed).length || 0);

      // Check milestone achievements (6, 12, 19 levels)
      const milestones = [
        { count: 6, id: 'first_steps', title: 'Primeros Pasos', coins: 100 },
        { count: 12, id: 'constructor_milestone', title: 'Constructor Experto', coins: 200 },
        { count: 19, id: 'master', title: 'Maestro de n8n', coins: 500 }
      ];

      let bonusCoins = 0;
      for (const milestone of milestones) {
        if (completedCount >= milestone.count && !unlockedIds.has(milestone.id)) {
          await db.achievements.unlock(user.id, milestone.id);
          bonusCoins += milestone.coins;
          toast.success(`🏆 ¡Logro desbloqueado: ${milestone.title}!`);
        }
      }

      // Add bonus coins if any achievements were unlocked
      if (bonusCoins > 0) {
        await db.profile.update(user.id, {
          coins: newCoins + bonusCoins
        });
      }

      // Mark concepts as learned
      if (level.concepts_taught && level.concepts_taught.length > 0) {
        for (const conceptId of level.concepts_taught) {
          try {
            await db.concepts.markLearned(user.id, conceptId);
          } catch (error) {
            // Concept might already be marked as learned, that's ok
          }
        }
      }

      // CHECK IF LEVEL 19 (GAME COMPLETION)
      if (parseInt(levelId) === 19) {
        setShowQuiz(false);
        setShowGameCompletionModal(true);
      } else {
        // Normal level completion (levels 1-18)
        setShowQuiz(false);
        setShowVictory(true);

        // Trigger: LEVEL_COMPLETED
        checkTriggers({
          type: 'LEVEL_COMPLETED',
          data: {
            levelsCompleted: completedCount,
            currentLevel: parseInt(levelId)
          }
        });

        // Trigger: XP_MILESTONE
        checkTriggers({
          type: 'XP_MILESTONE',
          data: {
            totalXp: newTotalXp
          }
        });
      }

      toast.success(`¡Excelente! Ganaste ${xpGained} XP y ${coinsEarned + bonusCoins} monedas`);
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Error al guardar el progreso');
    }
  };
  
  const handleQuizReview = () => {
    setShowQuiz(false);
    toast.info('Revisa el nivel y vuelve a validar cuando est\u00e9s listo');
  };

  const handleNextLevel = () => {
    const nextLevelId = parseInt(levelId) + 1;
    if (nextLevelId <= 12) {
      navigate(`/level/${nextLevelId}`);
      setShowVictory(false);
      setShowQuiz(false);
      setQuizPassed(false);
      setNodes([]);
      setEdges([]);
      setValidationResult(null);
      setTutorialStep(0);
    } else {
      navigate('/dashboard');
    }
  };

  const nextTutorialStep = () => {
    if (tutorialStep < (level?.tutorial_steps?.length || 0) - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      setShowTutorial(false);
    }
  };

  const skipTutorial = () => {
    setShowTutorial(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-heading">Cargando nivel...</div>
      </div>
    );
  }

  const availableNodeTypes = level?.success_criteria?.required_nodes || [];

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden" data-testid="level-play">
      {/* Header */}
      <header className="glass border-b border-white/10 backdrop-blur-lg z-50">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-text-secondary hover:text-primary transition-colors flex-shrink-0"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-sm sm:text-base md:text-2xl font-heading font-bold text-text-primary truncate">{level?.title}</h1>
                <p className="text-xs md:text-sm text-text-secondary hidden sm:block truncate">{level?.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <button
                onClick={handleGetHint}
                disabled={hintLoading}
                className="glass px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg hover:border-primary/50 transition-all flex items-center gap-1 sm:gap-2"
                data-testid="hint-btn"
                title="Pista"
              >
                <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                <span className="text-text-primary text-xs sm:text-sm hidden md:inline">Pista</span>
              </button>
              <button
                onClick={handleReset}
                className="glass px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg hover:border-primary/50 transition-all flex items-center gap-1 sm:gap-2"
                data-testid="reset-btn"
                title="Reiniciar"
              >
                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 text-text-primary" />
                <span className="text-text-primary text-xs sm:text-sm hidden md:inline">Reiniciar</span>
              </button>
              <button
                onClick={handleValidate}
                disabled={validating || nodes.length === 0}
                className="btn-neon px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-button font-heading font-bold text-xs sm:text-sm md:text-base flex items-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="validate-btn"
              >
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{validating ? 'Validando...' : 'Validar'}</span>
                <span className="sm:hidden">{validating ? '...' : '✓'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Toggle button for mobile */}
        <button
          onClick={() => setShowNodeLibrary(!showNodeLibrary)}
          className="md:hidden absolute top-3 left-3 z-30 glass p-2 rounded-lg hover:border-primary/50 transition-all"
          title="Nodos disponibles"
        >
          <Menu className="w-4 h-4 text-text-primary" />
        </button>

        {/* Node Library Sidebar - Overlay on mobile, static on desktop */}

        {/* Overlay for mobile */}
        <AnimatePresence>
          {showNodeLibrary && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed top-14 md:top-[73px] left-0 right-0 bottom-0 bg-black/50 z-[55]"
              onClick={() => setShowNodeLibrary(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar - Animated on mobile, static on desktop */}

        {/* Desktop sidebar - always visible, no animation */}
        <aside className="hidden md:block md:relative h-full w-56 lg:w-64 glass border-r border-white/10 p-4 overflow-y-auto">
          <NodeLibrary nodeTypes={nodeTypes} availableTypes={availableNodeTypes} />
        </aside>

        {/* Mobile sidebar - animated slide-in */}
        <AnimatePresence>
          {showNodeLibrary && (
            <motion.aside
              initial={{ x: -280, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -280, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 280,
                damping: 28,
                mass: 0.8,
                opacity: { duration: 0.2 }
              }}
              className="md:hidden fixed top-14 left-0 h-[calc(100vh-3.5rem)] w-56 sm:w-64 glass border-r border-white/10 p-3 sm:p-4 overflow-y-auto z-[60]"
            >
              {/* Close button for mobile */}
              <button
                onClick={() => setShowNodeLibrary(false)}
                className="absolute top-3 right-3 text-text-muted hover:text-error transition-colors"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <NodeLibrary nodeTypes={nodeTypes} availableTypes={availableNodeTypes} />
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Editor Canvas */}
        <main className="flex-1 relative w-full">
          <NodeEditor
            initialNodes={nodes}
            initialEdges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            availableNodeTypes={nodeTypes}
          />

          {/* Tutorial Overlay */}
          <AnimatePresence>
            {showTutorial && level?.tutorial_steps && level.tutorial_steps[tutorialStep] && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50"
                data-testid="tutorial-overlay"
              >
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  className="glass p-6 md:p-8 rounded-card w-full max-w-lg mx-4 md:min-w-[480px] min-h-[300px] flex flex-col"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-heading font-bold text-text-primary">
                      Paso {tutorialStep + 1} de {level.tutorial_steps.length}
                    </h3>
                    <button onClick={skipTutorial} className="text-text-muted hover:text-text-primary">
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="flex-1 flex items-start mb-6">
                    <p className="text-lg text-text-primary leading-relaxed">{level.tutorial_steps[tutorialStep].text}</p>
                  </div>
                  <div className="flex gap-3 mt-auto">
                    <button onClick={skipTutorial} className="flex-1 glass py-3 rounded-lg hover:border-primary/50 transition-all font-medium">
                      Saltar Tutorial
                    </button>
                    <button onClick={nextTutorialStep} className="flex-1 btn-neon py-3 rounded-lg font-medium">
                      {tutorialStep < level.tutorial_steps.length - 1 ? 'Siguiente' : 'Entendido'}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Validation Feedback */}
          <AnimatePresence>
            {validationResult && !validationResult.is_valid && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                className="absolute bottom-4 right-4 glass p-4 rounded-lg max-w-md z-40"
                data-testid="validation-feedback"
              >
                <h4 className="font-heading font-bold text-error mb-2">Errores:</h4>
                <ul className="space-y-1 text-sm text-text-secondary">
                  {validationResult.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Quiz Modal */}
      <AnimatePresence>
        {showQuiz && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background/95 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-y-auto"
            data-testid="quiz-modal"
          >
            <LevelQuiz 
              levelId={parseInt(levelId)} 
              onComplete={handleQuizComplete}
              onReview={handleQuizReview}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Victory Modal */}
      <AnimatePresence>
        {showVictory && quizPassed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background/90 backdrop-blur-md flex items-center justify-center z-50"
            data-testid="victory-modal"
          >
            <motion.div
              initial={{ scale: 0.5, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="glass p-12 rounded-card text-center max-w-lg"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="text-8xl mb-6"
              >
                ✅
              </motion.div>
              <h2 className="text-4xl font-heading font-bold text-text-primary mb-4">¡Nivel Completado!</h2>
              <p className="text-xl text-text-secondary mb-6">Ganaste {validationResult?.xp_earned} XP</p>
              <div className="flex gap-4">
                {parseInt(levelId) < 12 ? (
                  <button
                    onClick={handleNextLevel}
                    className="w-full btn-neon py-3 rounded-lg font-heading font-bold"
                    data-testid="next-level-btn"
                  >
                    Siguiente Nivel
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full btn-neon py-3 rounded-lg font-heading font-bold"
                    data-testid="back-to-dashboard-btn"
                  >
                    Volver al Dashboard
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Node Configuration Sidebar */}
      <NodeConfigSidebar
        nodeType={selectedNodeType}
        isOpen={showNodeConfig}
        onClose={() => setShowNodeConfig(false)}
      />

      {/* Game Completion Modal (Level 19) */}
      <GameCompletionModal
        isOpen={showGameCompletionModal}
        onClose={() => {
          setShowGameCompletionModal(false);
          navigate('/dashboard');
        }}
        userStats={{
          totalXp: userProfile?.total_xp || 0,
          coins: userProfile?.coins || 0,
          achievementsCount: userAchievements?.length || 0,
          conceptsLearned: learnedConcepts?.filter(c => c.is_learned)?.length || 0,
        }}
      />
    </div>
  );
};

export default LevelPlay;